import React, { useState, useMemo, useRef, useCallback } from "react";
import * as Astro from "../../astronomy.js";
import {
    PLANET_SYMBOLS,
    PLANETS,
} from "../../utils/constants.js";
import { styles as commonStyles } from "../../utils/styles.js";
import { Download, Target } from "lucide-react";
import SolarArcPredictTimeline from "./SolarArcPredictTimeline.jsx";

// アスペクト定義 (合, オポジション, スクエア, セミ・スクエア, セスキコードレート, クインカンクスに限定)
const ASPECT_DEFS = [
    { name: "Conjunction", symbol: "☌", angle: 0, orb: 8, color: "#333333", type: "Hard" },
    { name: "Opposition", symbol: "☍", angle: 180, orb: 8, color: "#e74c3c", type: "Hard" },
    { name: "Square", symbol: "□", angle: 90, orb: 7, color: "#e67e22", type: "Hard" },
    { name: "Semi-Square", symbol: "∠", angle: 45, orb: 1.5, color: "#e74c3c", type: "Hard" },
    { name: "Sesquiquadrate", symbol: "⚼", angle: 135, orb: 1.5, color: "#c0392b", type: "Hard" },
    { name: "Quincunx", symbol: "⚻", angle: 150, orb: 1.5, color: "#8e44ad", type: "Hard" },
];

const ZODIAC_SIGNS = [
    { symbol: "♈", name: "Aries" }, { symbol: "♉", name: "Taurus" }, { symbol: "♊", name: "Gemini" },
    { symbol: "♋", name: "Cancer" }, { symbol: "♌", name: "Leo" }, { symbol: "♍", name: "Virgo" },
    { symbol: "♎", name: "Libra" }, { symbol: "♏", name: "Scorpio" }, { symbol: "♐", name: "Sagittarius" },
    { symbol: "♑", name: "Capricorn" }, { symbol: "♒", name: "Aquarius" }, { symbol: "♓", name: "Pisces" },
];

const SIGN_COLORS_DEFAULT = {
    "♈": "#e74c3c", "♉": "#27ae60", "♊": "#f1c40f", "♋": "#3498db", "♌": "#e67e22", "♍": "#95a5a6",
    "♎": "#fd79a8", "♏": "#8e44ad", "♐": "#2980b9", "♑": "#2c3e50", "♒": "#00cec9", "♓": "#74b9ff",
};

// シンボルの追加定義
const ALL_SYMBOLS = {
    ...PLANET_SYMBOLS,
    ASC: "AC",
    MC: "MC",
};

/**
 * ソーラーアーク三重円コンポーネント (再調整版)
 */
export default function SolarArcMidpointDial(props) {
    const {
        natalPositions,
        natalDate,
        transitDate,
        planetColors = {},
        signColors = SIGN_COLORS_DEFAULT,
        isDarkMode,
        isMobile,
        selectedAsset,
        transitTz,
    } = props;

    const svgRef = useRef(null);
    const [chartMode, setChartMode] = useState("triple"); // triple, sa-natal, sa-only, personal-only
    const [dialMode, setDialMode] = useState("360"); // 360, 90

    // 1. 基本設定
    const size = 600;
    const center = size / 2;
    const outerRadius = size * 0.44;
    const signRadius = outerRadius - 24;
    const transitRadius = signRadius - 45;
    const saRadius = transitRadius - 55;
    const natalRadius = saRadius - 55;

    // 2. 補助関数
    const getCoords = useCallback((deg, r) => {
        let visualDeg = deg;
        if (dialMode === "90") {
             visualDeg = (deg % 90) * 4;
        }
        const rad = ((visualDeg - 90) * Math.PI) / 180;
        return { x: center + r * Math.cos(rad), y: center + r * Math.sin(rad) };
    }, [center, dialMode]);

    // 天体の重なり防止ロジック
    const getClusteredBodies = useCallback((positions, baseRadius, filterPersonal = false) => {
        if (!positions) return [];

        // ASC, MC を含むリストを作成
        let activeIds = [...PLANETS.slice(0, 10), "ASC", "MC"];

        if (filterPersonal) {
            activeIds = ["Sun", "Moon", "ASC", "MC"];
        }

        const bodies = activeIds
            .map((id) => ({ id: id, lon: positions[id] }))
            .filter((b) => b.lon !== undefined && b.lon !== null)
            .sort((a, b) => dialMode === "90" ? (a.lon % 90) - (b.lon % 90) : a.lon - b.lon);

        if (bodies.length === 0) return [];

        const clusters = [];
        let currentCluster = [];
        bodies.forEach((body) => {
            if (currentCluster.length === 0) currentCluster.push(body);
            else {
                const prev = currentCluster[currentCluster.length - 1];
                let diff;
                if (dialMode === "90") {
                    diff = Math.abs((body.lon % 90) - (prev.lon % 90));
                    if (diff > 45) diff = 90 - diff;
                } else {
                    diff = Math.abs(body.lon - prev.lon);
                }
                const threshold = dialMode === "90" ? 2.5 : 6;
                if (diff <= threshold) currentCluster.push(body);
                else {
                    clusters.push(currentCluster);
                    currentCluster = [body];
                }
            }
        });
        if (currentCluster.length > 0) clusters.push(currentCluster);

        const results = [];
        clusters.forEach((cluster) => {
            const centerOffset = (cluster.length - 1) / 2;
            cluster.forEach((body, index) => {
                results.push({
                    ...body,
                    displayRadius: baseRadius + (index - centerOffset) * 20,
                    exactLon: body.lon,
                });
            });
        });
        return results;
    }, [dialMode]);

    // 3. 位置・クラスター計算
    const saPositions = useMemo(() => Astro.getSolarArcPositions(natalDate, transitDate, natalPositions), [natalDate, transitDate, natalPositions]);
    const transitPositionsRaw = useMemo(() => Astro.getAllPlanetLongitudes(transitDate), [transitDate]);

    const isPersonalOnly = chartMode === "personal-only";

    const transitBodies = useMemo(() => getClusteredBodies(transitPositionsRaw, transitRadius, isPersonalOnly), [transitPositionsRaw, getClusteredBodies, transitRadius, isPersonalOnly]);
    const saBodies = useMemo(() => getClusteredBodies(saPositions, saRadius, isPersonalOnly), [saPositions, getClusteredBodies, saRadius, isPersonalOnly]);
    const natalBodies = useMemo(() => getClusteredBodies(natalPositions, natalRadius, isPersonalOnly), [natalPositions, getClusteredBodies, natalRadius, isPersonalOnly]);

    // 4. アスペクトライン
    const aspectLines = useMemo(() => {
        const results = [];
        const check = (set1, set2, r1, r2, orbLimit) => {
            set1.forEach(b1 => {
                set2.forEach(b2 => {
                    let diff = Math.abs(b1.exactLon - b2.exactLon);
                    if (diff > 180) diff = 360 - diff;
                    ASPECT_DEFS.forEach(asp => {
                        if (Math.abs(diff - asp.angle) <= orbLimit) {
                            const p1 = getCoords(b1.exactLon, r1);
                            const p2 = getCoords(b2.exactLon, r2);
                            results.push({ x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, symbol: asp.symbol, color: asp.color });
                        }
                    });
                });
            });
        };

        // SA × Natal
        if (chartMode !== "sa-only") {
            check(saBodies, natalBodies, saRadius, natalRadius, 0.5);
        }

        // Transit × Natal (SA-Only の時は表示しない)
        if (chartMode === "triple" || chartMode === "personal-only") {
            check(transitBodies, natalBodies, transitRadius, natalRadius, 0.5);
        }

        return results;
    }, [chartMode, saBodies, natalBodies, transitBodies, saRadius, natalRadius, transitRadius, getCoords]);

    // 5. ラベル・デザイン
    const birthLabel = useMemo(() => {
        if (!selectedAsset) return "";
        const tzStr = (selectedAsset.timezone >= 0 ? "+" : "") + selectedAsset.timezone;
        return `${selectedAsset.date.replace(/-/g, "/")} ${selectedAsset.time} UTC${tzStr}`;
    }, [selectedAsset]);

    const transitLabel = useMemo(() => {
        if (!transitDate) return "";
        const y = transitDate.getUTCFullYear();
        const m = (transitDate.getUTCMonth() + 1).toString().padStart(2, '0');
        const d = transitDate.getUTCDate().toString().padStart(2, '0');
        const hh = transitDate.getUTCHours().toString().padStart(2, '0');
        const mm = transitDate.getUTCMinutes().toString().padStart(2, '0');
        const tzStr = (transitTz >= 0 ? "+" : "") + transitTz;
        return `${y}/${m}/${d} ${hh}:${mm} UTC${tzStr}`;
    }, [transitDate, transitTz]);

    const saveAsImage = () => {
        if (!svgRef.current) return;
        const svgData = new XMLSerializer().serializeToString(svgRef.current);
        const canvas = document.createElement("canvas");
        canvas.width = 1200; canvas.height = 1200;
        const ctx = canvas.getContext("2d");
        const img = new Image();
        img.onload = () => {
            ctx.fillStyle = isDarkMode ? "#1a191e" : "white";
            ctx.fillRect(0, 0, 1200, 1200);
            ctx.drawImage(img, 0, 0, 1200, 1200);

            const getFormatDate = (label) => {
                if (!label) return "";
                const match = label.match(/(\d{4})\/(\d{2})\/(\d{2})/);
                return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
            };
            
            const bDate = getFormatDate(birthLabel);
            const tDate = getFormatDate(transitLabel);
            let filename = "solararc";
            if (bDate) filename += `_${bDate}`;
            if (tDate) filename += `_${tDate}`;
            filename += ".png";

            const link = document.createElement("a");
            link.download = filename;
            link.href = canvas.toDataURL("image/png");
            link.click();
        };
        img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    };

    const renderDegreeColumn = (title, positions) => (
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <div style={{ textAlign: "center", fontSize: "10px", fontWeight: "bold", color: isDarkMode ? "#888" : "#aaa", marginBottom: "8px", letterSpacing: "0.1em" }}>
                {title.toUpperCase()}
            </div>
            <div style={{ background: isDarkMode ? "rgba(255, 255, 255, 0.01)" : "#fff", borderRadius: "12px", border: isDarkMode ? "1px solid rgba(255,255,255,0.05)" : "1px solid #f0f0f0", padding: "10px" }}>
                {[...PLANETS.slice(0, 10), "ASC", "MC"].map(id => {
                    const pos = positions[id];
                    if (pos == null) return null;
                    const sign = Astro.getZodiacSign(pos);
                    const degInSign = pos % 30;
                    const d = Math.floor(degInSign);
                    const m = Math.floor((degInSign - d) * 60);
                    const s = Math.floor(((degInSign - d) * 60 - m) * 60);
                    return (
                        <div key={id} style={{ display: "flex", alignItems: "center", padding: "5px 0", borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.03)" : "1px solid #f9f9f9", fontSize: "11px" }}>
                            <div style={{ display: "flex", alignItems: "center", width: "40px", flexShrink: 0 }}>
                                <span style={{ fontSize: id === "ASC" || id === "MC" ? "14px" : "20px", color: planetColors[id] || (isDarkMode ? "#fff" : "#333"), fontWeight: "bold" }}>
                                    {ALL_SYMBOLS[id] || id}
                                </span>
                            </div>
                            <span style={{ color: isDarkMode ? "#aaa" : "#888", width: "55px", fontWeight: "bold" }}>{id}</span>
                            <span style={{ fontSize: "20px", color: signColors[sign.symbol] || SIGN_COLORS_DEFAULT[sign.symbol], marginRight: "6px", display: "inline-flex", alignItems: "center" }}>{sign.symbol + "\uFE0E"}</span>
                            <span style={{ fontWeight: "normal", color: isDarkMode ? "#fff" : "#1e293b", marginRight: "2px" }}>{d}°</span>
                            <span style={{ color: isDarkMode ? "#888" : "#64748b", fontWeight: "normal" }}>{m.toString().padStart(2, "0")}'{s.toString().padStart(2, "0")}"</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "7fr 5fr", gap: "20px", width: "100%" }}>
                {/* 左側: チャート */}
                <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
                    <div style={commonStyles.dynamicBlockTitle(isDarkMode, isMobile)}>
                        <Target size={18} color="#e67e22" />
                        <span>ソーラーアーク</span>
                    </div>

                    <div style={{ display: "flex", gap: "10px", padding: "15px", flexWrap: "wrap", justifyContent: "center" }}>
                        <select value={chartMode} onChange={(e) => setChartMode(e.target.value)} style={{ padding: "8px 12px", borderRadius: "20px", fontSize: "12px", background: isDarkMode ? "#2d2c33" : "#fff", color: isDarkMode ? "#fff" : "#333", border: "1px solid " + (isDarkMode ? "#444" : "#ddd"), outline: "none", cursor: "pointer", fontWeight: "bold" }}>
                            <option value="triple">ソーラーアーク×ネイタル×トランジット(三重円)</option>
                            <option value="sa-natal">ソーラーアーク×ネイタル</option>
                            <option value="sa-only">ソーラーアークのみ</option>
                            <option value="personal-only">パーソナル天体のみ</option>
                        </select>
                        <select value={dialMode} onChange={(e) => setDialMode(e.target.value)} style={{ padding: "8px 12px", borderRadius: "20px", fontSize: "12px", background: isDarkMode ? "#2d2c33" : "#fff", color: isDarkMode ? "#fff" : "#333", border: "1px solid " + (isDarkMode ? "#444" : "#ddd"), outline: "none", cursor: "pointer", fontWeight: "bold" }}>
                            <option value="360">360°チャート</option>
                            <option value="90">90°チャート</option>
                        </select>
                    </div>

                    <div style={{ padding: "10px", textAlign: "center" }}>
                        <svg ref={svgRef} viewBox="0 0 600 600" style={{ maxWidth: "100%", height: "auto" }}>
                            <g style={{ userSelect: "none" }}>
                                <text x="0" y="12" fontSize="11" fill={isDarkMode ? "#ffffff" : "#5f5f5f"} fontWeight="normal" textAnchor="start">
                                    <tspan x="0" dy="0" fontWeight="bold">Inner Wheel</tspan>
                                    {selectedAsset?.name && <tspan x="0" dy="14" fontWeight="bold">{selectedAsset.name}</tspan>}
                                    <tspan x="0" dy="14">Natal Chart</tspan>
                                    {birthLabel ? (birthLabel.split(' UTC').length > 1 ? (
                                        <>
                                            <tspan x="0" dy="14">{birthLabel.split(' UTC')[0]}</tspan>
                                            <tspan x="0" dy="14">UTC{birthLabel.split(' UTC')[1]}</tspan>
                                        </>
                                    ) : (
                                        <tspan x="0" dy="14">{birthLabel}</tspan>
                                    )) : null}
                                </text>

                                <text x={size} y="12" fontSize="11" fill={isDarkMode ? "#ffffff" : "#5f5f5f"} fontWeight="normal" textAnchor="end">
                                    <tspan x={size} dy="0" fontWeight="bold">Outer Wheel</tspan>
                                    <tspan x={size} dy="14">Transit Chart</tspan>
                                    {transitLabel ? (transitLabel.split(' UTC').length > 1 ? (
                                        <>
                                            <tspan x={size} dy="14">{transitLabel.split(' UTC')[0]}</tspan>
                                            <tspan x={size} dy="14">UTC{transitLabel.split(' UTC')[1]}</tspan>
                                        </>
                                    ) : (
                                        <tspan x={size} dy="14">{transitLabel}</tspan>
                                    )) : null}
                                </text>

                                <text x={size} y={size - 20} fontSize="11" fill={isDarkMode ? "#ffffff" : "#5f5f5f"} fontWeight="normal" textAnchor="end">
                                    <tspan x={size} dy="0" fontWeight="bold">Middle Wheel</tspan>
                                    <tspan x={size} dy="14">Solar Arc Chart</tspan>
                                </text>
                            </g>

                            {/* 外周の目盛り / サイン */}
                            {dialMode === "360" ? (
                                <>
                                    {/* 360度の目盛り */}
                                    {Array.from({ length: 360 }).map((_, deg) => {
                                        const isMajorTick = deg % 30 === 0;
                                        const isDecanTick = deg % 10 === 0 && !isMajorTick;
                                        const isMediumTick = deg % 5 === 0 && !isMajorTick && !isDecanTick;
                                        const tickInnerR = isMajorTick ? outerRadius - 14 : isDecanTick ? outerRadius - 10 : isMediumTick ? outerRadius - 7 : outerRadius - 4;
                                        const p1 = getCoords(deg, outerRadius);
                                        const p2 = getCoords(deg, tickInnerR);
                                        const labelPos = getCoords(deg, outerRadius + 14);
                                        return (
                                            <g key={deg}>
                                                <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={isDarkMode ? (isMajorTick ? "#ffffff" : "rgba(255,255,255,0.3)") : (isMajorTick ? "#1e293b" : "#cbd5e1")} strokeWidth={isMajorTick ? 2 : 1} />
                                                {isMajorTick && (
                                                    <text x={labelPos.x} y={labelPos.y} fontSize="10" textAnchor="middle" dominantBaseline="middle" fill={isDarkMode ? "#ffffff" : "#475569"} fontWeight="900">{deg}°</text>
                                                )}
                                            </g>
                                        );
                                    })}

                                    {/* サイン境界線 */}
                                    {Array.from({ length: 12 }).map((_, i) => {
                                        const p1 = getCoords(i * 30, outerRadius);
                                        const p2 = getCoords(i * 30, natalRadius - 30);
                                        return <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={isDarkMode ? "rgba(255,255,255,0.1)" : "#f1f5f9"} strokeWidth="1.5" />;
                                    })}

                                    {/* 星座アイコン */}
                                    {ZODIAC_SIGNS.map((sign, i) => {
                                        const pos = getCoords(i * 30 + 15, outerRadius + 35);
                                        return <text key={i} x={pos.x} y={pos.y} fontSize="14" textAnchor="middle" dominantBaseline="middle" fill={signColors[sign.symbol] || SIGN_COLORS_DEFAULT[sign.symbol]}>{sign.symbol + "\uFE0E"}</text>;
                                    })}
                                </>
                            ) : (
                                <>
                                    {/* 90度の目盛り */}
                                    {Array.from({ length: 90 }).map((_, deg) => {
                                        const isMajor = deg % 10 === 0;
                                        const isMedium = deg % 5 === 0 && !isMajor;
                                        const tickLen = isMajor ? 12 : isMedium ? 8 : 4;
                                        const tickInnerR = outerRadius - tickLen;
                                        const p1 = getCoords(deg, outerRadius);
                                        const p2 = getCoords(deg, tickInnerR);
                                        const labelPos = getCoords(deg, outerRadius + 14);
                                        return (
                                            <g key={"tick-" + deg}>
                                                <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={isDarkMode ? (isMajor ? "#ffffff" : "rgba(255,255,255,0.3)") : (isMajor ? "#1e293b" : "#cbd5e1")} strokeWidth={isMajor ? 2 : 1} />
                                                {isMajor && (
                                                    <text x={labelPos.x} y={labelPos.y} fontSize="10" textAnchor="middle" dominantBaseline="middle" fill={isDarkMode ? "#ffffff" : "#475569"} fontWeight="900">{deg}°</text>
                                                )}
                                            </g>
                                        );
                                    })}

                                    {/* モダリティ境界線とラベル */}
                                    {["Cardinal", "Fixed", "Mutable"].map((mod, idx) => {
                                        const startDeg = idx * 30;
                                        const midDeg = startDeg + 15;
                                        const pStart = getCoords(startDeg, outerRadius);
                                        const pEnd = getCoords(startDeg, natalRadius - 30);
                                        const pMidLab = getCoords(midDeg, outerRadius - 28);
                                        const qualityStyle = {
                                            Cardinal: { color: "#8b5cf6", label: "CARDINAL(活動宮)" },
                                            Fixed: { color: "#c77a0a", label: "FIXED(固定宮)" },
                                            Mutable: { color: "#27ae60", label: "MUTABLE(柔軟宮)" }
                                        };
                                        return (
                                            <g key={mod}>
                                                <line x1={pStart.x} y1={pStart.y} x2={pEnd.x} y2={pEnd.y} stroke={isDarkMode ? "rgba(255,255,255,0.1)" : "#cbd5e1"} strokeWidth="1.5" strokeDasharray="4,4" />
                                                <g transform={`translate(${pMidLab.x}, ${pMidLab.y}) rotate(${(midDeg % 90) * 4})`}>
                                                    <text x="0" y="0" fontSize="10" fontWeight="bold" fill={qualityStyle[mod].color} textAnchor="middle" dominantBaseline="middle">
                                                        {qualityStyle[mod].label}
                                                    </text>
                                                </g>
                                            </g>
                                        );
                                    })}
                                </>
                            )}

                            {/* サークルリング */}
                            {(chartMode === "triple" || chartMode === "personal-only") && (
                                <circle cx={center} cy={center} r={transitRadius} fill="none" stroke="#2c2d96" strokeWidth="1.2" opacity="0.4" />
                            )}
                            <circle cx={center} cy={center} r={saRadius} fill="none" stroke="#e67e22" strokeWidth="1.2" strokeDasharray="4,4" opacity="0.4" />
                            {(chartMode !== "sa-only") && (
                                <circle cx={center} cy={center} r={natalRadius} fill="none" stroke="#9c2393" strokeWidth="1.2" strokeDasharray="4,4" opacity="0.4" />
                            )}

                            {/* アスペクトライン */}
                            {aspectLines.map((line, i) => (
                                <g key={i}>
                                    <line x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke={line.color} strokeWidth="1" opacity="0.5" />
                                    <g transform={`translate(${(line.x1 + line.x2) / 2}, ${(line.y1 + line.y2) / 2})`}>
                                        <circle r="8" fill={isDarkMode ? "#1a191e" : "white"} stroke={line.color} strokeWidth="0.5" />
                                        <text dy="3.5" fontSize="10" textAnchor="middle" fill={line.color}>{line.symbol}</text>
                                    </g>
                                </g>
                            ))}

                            {/* 天体アイコン */}
                            {(() => {
                                const renderIcons = (bodies, color) => bodies.map(b => {
                                    const pos = getCoords(b.lon, b.displayRadius);
                                    return (
                                        <g key={b.id}>
                                            <circle cx={pos.x} cy={pos.y} r="10" fill={isDarkMode ? "rgba(255,255,255,0.1)" : "white"} stroke={color} strokeWidth="0.5" />
                                            <text x={pos.x} y={pos.y} fontSize={b.id === "ASC" || b.id === "MC" ? "10" : "14"} textAnchor="middle" dominantBaseline="middle" fill={planetColors[b.id] || color} fontWeight="bold">
                                                {ALL_SYMBOLS[b.id] || b.id}
                                            </text>
                                        </g>
                                    );
                                });

                                return (
                                    <g>
                                        {(chartMode !== "sa-only") && natalBodies.length > 0 && renderIcons(natalBodies, "#9c2393")}
                                        {saBodies.length > 0 && renderIcons(saBodies, "#e67e22")}
                                        {(chartMode === "triple" || chartMode === "personal-only") && transitBodies.length > 0 && renderIcons(transitBodies, "#2c2d96")}
                                    </g>
                                );
                            })()}
                        </svg>

                        {/* 凡例 & リンク */}
                        <div style={{ marginTop: "20px", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                            <div style={{ display: "flex", justifyContent: "center", gap: "15px", fontSize: "11px", flexWrap: "wrap", color: isDarkMode ? "#aaa" : "#666", fontWeight: "bold" }}>
                                {(chartMode === "triple" || chartMode === "personal-only") && (
                                    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                        <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#2c2d96" }} /> Transit (Blue Solid)
                                    </span>
                                )}
                                {(chartMode !== "sa-only") && (
                                    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                        <span style={{ width: "8px", height: "8px", borderRadius: "50%", border: "1px dashed #9c2393", background: "none" }} /> Natal (Purple Dashed)
                                    </span>
                                )}
                                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                    <span style={{ width: "8px", height: "8px", borderRadius: "50%", border: "1px dashed #e67e22", background: "none" }} /> Solar Arc (Orange Dashed)
                                </span>
                            </div>
                            <button onClick={saveAsImage} style={{ background: "none", border: "none", color: isDarkMode ? "#ffffff" : "#2c2d96", display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: "bold", cursor: "pointer" }}>
                                <Download size={18} /> Download Chart
                            </button>
                        </div>
                    </div>
                </div>

                {/* 右側: 度数テーブル */}
                <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
                    <div style={commonStyles.dynamicBlockTitle(isDarkMode, isMobile)}>
                        <Target size={18} color="#e74c3c" />
                        <span>Birth / Solar Arc 度数</span>
                    </div>
                    <div style={{ display: "flex", gap: "10px", padding: "10px" }}>
                        {renderDegreeColumn("Birth", natalPositions)}
                        {renderDegreeColumn("Solar Arc", saPositions)}
                    </div>
                </div>
            </div>

            <SolarArcPredictTimeline
                natalDate={natalDate}
                transitDate={transitDate}
                natalPositions={natalPositions}
                isDarkMode={isDarkMode}
                planetColors={planetColors}
                signColors={signColors}
                isMobile={isMobile}
            />
        </div>
    );
}
